import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Edit,
  AlertCircle,
} from "lucide-react";
import { toast } from "react-toastify";
import VendorForm from "./VendorForm";
import { vendorAPI } from "../../services";
import { useSelector } from "react-redux";
import MaskedCurrencyValue from "../common/MaskedCurrencyValue";

const VendorDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isPartsURL, isAccessoriesURL } = useSelector((state) => state.domain);
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  
  // Ref to prevent double fetching in React StrictMode
  const initialFetchDone = useRef(false);

  useEffect(() => {
    const fetchVendor = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await vendorAPI.getVendorById(id);
        setVendor(response.data);
      } catch (error) {
        console.error("Error loading vendor:", error);
        setError("Failed to load Supplier details. Please try again.");
        
        // Fallback to localStorage if API fails
        const storedVendors = localStorage.getItem("vendors");
        if (storedVendors) {
          try {
            const parsedVendors = JSON.parse(storedVendors);
            const foundVendor = parsedVendors.find((v) => v.id === id);
            if (foundVendor) {
              setVendor(foundVendor);
              setError(null);
            }
          } catch (parseError) {
            console.error("Error parsing stored vendors:", parseError);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    // Prevent double fetch in React StrictMode
    // Only fetch once per id change
    const currentId = id;
    if (!initialFetchDone.current || initialFetchDone.current !== currentId) {
      initialFetchDone.current = currentId;
      fetchVendor();
    }
  }, [id]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleEditVendor = () => {
    setShowEditForm(true);
  };

  const handleEditVendorSave = async (vendorData) => {
    try {
      setError(null);
      // Call the update Supplier API
      const response = await vendorAPI.updateVendor(id, vendorData);
      const updatedVendor = response.data || response;
      
      // Update the local state with the response from API
      setVendor(updatedVendor);
      
      // Also update localStorage as backup
      const storedVendors = localStorage.getItem("vendors");
      if (storedVendors) {
        try {
          const parsedVendors = JSON.parse(storedVendors);
          const updatedVendors = parsedVendors.map((v) =>
            v.id === id ? updatedVendor : v
          );
          localStorage.setItem("vendors", JSON.stringify(updatedVendors));
        } catch (parseError) {
          console.error("Error updating Supplier in localStorage:", parseError);
        }
      }
      
      setShowEditForm(false);
      toast.success("Supplier updated successfully!");
    } catch (error) {
      console.error("Error updating Supplier:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to update Supplier. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage);
      
      // Fallback to localStorage only
      const updatedVendor = {
        ...vendor,
        ...vendorData,
        updated_at: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      setVendor(updatedVendor);

      const storedVendors = localStorage.getItem("vendors");
      if (storedVendors) {
        try {
          const parsedVendors = JSON.parse(storedVendors);
          const updatedVendors = parsedVendors.map((v) =>
            v.id === id ? updatedVendor : v
          );
          localStorage.setItem("vendors", JSON.stringify(updatedVendors));
        } catch (parseError) {
          console.error("Error updating Supplier in localStorage:", parseError);
        }
      }
      
      setShowEditForm(false);
    }
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "400px" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="card shadow-sm rounded-3 p-4">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "400px" }}
        >
          <div className="text-center">
            <AlertCircle size={48} className="text-muted mb-3" />
            <h4 className="text-muted">Supplier not found</h4>
            <p className="text-muted">
              The Supplier you're looking for doesn't exist.
            </p>
            <button
              className="btn btn-primary"
              onClick={() => navigate("/vendors")}
            >
              Back to Suppliers
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="d-flex align-items-center mb-2">
        <button
          className="btn text-decoration-none me-3 p-0"
          onClick={() => navigate("/vendors")}
          style={{ color: isPartsURL ? "var(--primary-color-parts)" : isAccessoriesURL ? "var(--primary-color-acc)" : "var(--primary-color-mobile)", fontWeight: "400", fontSize: "13px" }}
        >
          <ArrowLeft size={15} className="me-2" />
          Back to Suppliers
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="alert alert-danger mb-3" role="alert">
          <div className="d-flex align-items-center">
            <span className="me-2">⚠️</span>
            {error}
            <button
              className="btn btn-sm btn-outline-danger ms-auto"
              onClick={() => setError(null)}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      <div
        className="shadow-sm p-4"
        style={{ borderRadius: "14px", background: "#fff" }}
      >
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          {/* Supplier Title */}
          <div className="mb-4">
            <h1
              className="h2 fw-semi-bold mb-2"
              style={{
                fontWeight: "600",
                fontSize: "22px",
                color: "#424A53",
                lineHeight: "100%",
              }}
            >
              {vendor.name}
            </h1>
            <p
              className="text-muted mb-0"
              style={{ color: "#6E7781", fontWeight: "400", fontSize: "14px" }}
            >
              Supplier details and contact information
            </p>
          </div>

          <button
            className="d-flex align-items-center"
            onClick={handleEditVendor}
            style={{
              backgroundColor: isPartsURL ? "var(--primary-color-parts)" : isAccessoriesURL ? "var(--primary-color-acc)" : "var(--primary-color-mobile)",
              color: "#fff",
              fontWeight: "400",
              fontSize: "14px",
              borderRadius: "6px",
              padding: "10px",
              border: "none",
            }}
          >
            <Edit size={16} className="me-2" />
            Edit Supplier
          </button>
        </div>

        <div className="row">
          {/* Left Column - Statistics and Activity */}
          <div className="col-lg-8">
            {/* Statistics Card */}
            <div
              className="mb-4"
              style={{
                border: "1px solid #DCE3EB",
                borderRadius: "14px",
                background: "#fff",
                padding: "16px",
              }}
            >
              <div className="card-header bg-white border-0 pb-0">
                <h5
                  className="mb-0"
                  style={{
                    color: "#424A53",
                    fontWeight: "500",
                    fontSize: "18px",
                    paddingBottom: "16px",
                  }}
                >
                  Statistics
                </h5>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  {/* Total Order: Linear Gradient 1st #FFFFFF 2nd #E3E8FF */}
                  <div className="col-md-4">
                    <div
                      className="text-center p-3"
                      style={{
                        background:
                          "linear-gradient(to left, #FFFFFF, #E3E8FF)",
                        border: "1px solid #DCE3EB",
                        borderRadius: "8px",
                      }}
                    >
                      <h6
                        className="text-muted small mb-1"
                        style={{
                          color: "#6E7781",
                          fontWeight: "400",
                          fontSize: "14px",
                        }}
                      >
                        Total Order
                      </h6>
                      <h4
                        className="mb-0"
                        style={{
                          color: "#424A53",
                          fontWeight: "600",
                          fontSize: "28px",
                        }}
                      >
                        {vendor.total_products || 0}
                      </h4>
                    </div>
                  </div>
                  {/* Total Value: Linear Gradient 1st #FFFFFF 2nd ##E5FFE3 */}
                  <div className="col-md-4">
                    <div
                      className="text-center p-3 border rounded"
                      style={{
                        background:
                          "linear-gradient(to left, #FFFFFF, #E5FFE3)",
                        border: "1px solid #DCE3EB",
                        borderRadius: "8px",
                      }}
                    >
                      <h6
                        className="text-muted small mb-1"
                        style={{
                          color: "#6E7781",
                          fontWeight: "400",
                          fontSize: "14px",
                        }}
                      >
                        Total Value
                      </h6>
                      <h4
                        className="mb-0"
                        style={{
                          color: "#424A53",
                          fontWeight: "600",
                          fontSize: "28px",
                        }}
                      >
                        <MaskedCurrencyValue
                          value={`€ ${
                            vendor.total_value
                              ? vendor.total_value.toFixed(2)
                              : "0.00"
                          }`}
                        />
                      </h4>
                    </div>
                  </div>
                  {/* Total Quantity: Linear Gradient 1st #FFFFFF 2nd ##E3F3FF */}
                  <div className="col-md-4">
                    <div
                      className="text-center p-3 border rounded"
                      style={{
                        background:
                          "linear-gradient(to left, #FFFFFF, #E3F3FF)",
                        border: "1px solid #DCE3EB",
                        borderRadius: "8px",
                      }}
                    >
                      <h6
                        className="text-muted small mb-1"
                        style={{
                          color: "#6E7781",
                          fontWeight: "400",
                          fontSize: "14px",
                        }}
                      >
                        Total Quantity
                      </h6>
                      <h4
                        className="mb-0"
                        style={{
                          color: "#424A53",
                          fontWeight: "600",
                          fontSize: "28px",
                        }}
                      >
                        {vendor.total_quantity || 0}
                      </h4>
                    </div>
                  </div>
                  {/* Total Returns: Linear Gradient 1st #FFFFFF 2nd ##FFE3E4 */}
                  <div className="col-md-4">
                    <div
                      className="text-center p-3 border rounded"
                      style={{
                        background:
                          "linear-gradient(to left, #FFFFFF, #FFE3E4)",
                        border: "1px solid #DCE3EB",
                        borderRadius: "8px",
                      }}
                    >
                      <h6
                        className="text-muted small mb-1"
                        style={{
                          color: "#6E7781",
                          fontWeight: "400",
                          fontSize: "14px",
                        }}
                      >
                        Total Returns
                      </h6>
                      <h4
                        className="mb-0"
                        style={{
                          color: "#424A53",
                          fontWeight: "600",
                          fontSize: "28px",
                        }}
                      >
                        {vendor.total_return || 0}
                      </h4>
                    </div>
                  </div>
                  {/* Success Return: Linear Gradient 1st #FFFFFF 2nd ##E3FDFF */}
                  <div className="col-md-4">
                    <div
                      className="text-center p-3 border rounded"
                      style={{
                        background:
                          "linear-gradient(to left, #FFFFFF, #E3FDFF)",
                        border: "1px solid #DCE3EB",
                        borderRadius: "8px",
                      }}
                    >
                      <h6
                        className="text-muted small mb-1"
                        style={{
                          color: "#6E7781",
                          fontWeight: "400",
                          fontSize: "14px",
                        }}
                      >
                        Success Return
                      </h6>
                      <h4
                        className="mb-0"
                        style={{
                          color: "#424A53",
                          fontWeight: "600",
                          fontSize: "28px",
                        }}
                      >
                        {vendor.success_return || 0}
                      </h4>
                    </div>
                  </div>
                  {/* Pending Return: Linear Gradient 1st #FFFFFF 2nd #FFF6E3 */}
                  <div className="col-md-4">
                    <div
                      className="text-center p-3 border rounded"
                      style={{
                        background:
                          "linear-gradient(to left, #FFFFFF, #FFF6E3)",
                        border: "1px solid #DCE3EB",
                        borderRadius: "8px",
                      }}
                    >
                      <h6
                        className="text-muted small mb-1"
                        style={{
                          color: "#6E7781",
                          fontWeight: "400",
                          fontSize: "14px",
                        }}
                      >
                        Pending Return
                      </h6>
                      <h4
                        className="mb-0"
                        style={{
                          color: "#424A53",
                          fontWeight: "600",
                          fontSize: "28px",
                        }}
                      >
                        {vendor.pending_return || 0}
                      </h4>
                    </div>
                  </div>
                  {/* Average Time: Linear Gradient 1st #FFFFFF 2nd #FFE3FF */}
                  <div className="col-md-4">
                    <div
                      className="text-center p-3 border rounded"
                      style={{
                        background:
                          "linear-gradient(to left, #FFFFFF, #FFE3FF)",
                        border: "1px solid #DCE3EB",
                        borderRadius: "8px",
                      }}
                    >
                      <h6
                        className="text-muted small mb-1"
                        style={{
                          color: "#6E7781",
                          fontWeight: "400",
                          fontSize: "14px",
                        }}
                      >
                        Average Time
                      </h6>
                      <h4
                        className="mb-0"
                        style={{
                          color: "#424A53",
                          fontWeight: "600",
                          fontSize: "28px",
                        }}
                      >
                        {vendor.average_time || "0 days"}
                      </h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Activity & History Card */}
            <div
              className=""
              style={{
                border: "1px solid #DCE3EB",
                borderRadius: "14px",
                background: "#fff",
                padding: "16px",
              }}
            >
              <div className="card-header bg-white border-0 pb-0">
                <h5
                  className="mb-1"
                  style={{
                    color: "#424A53",
                    fontWeight: "500",
                    fontSize: "18px",
                    paddingBottom: "8px",
                  }}
                >
                  Activity & History
                </h5>
                <p
                  className="text-muted small mb-0"
                  style={{
                    color: "#6E7781",
                    fontWeight: "400",
                    fontSize: "12px",
                  }}
                >
                  Recent interactions and transaction history
                </p>
              </div>
              <div className="card-body">
                <div className="text-center py-5">
                  {/* <AlertCircle size={48} className="text-muted mb-3" /> */}
                  {/* <img src="/131.png" className="mb-4" /> */}
                  <h6
                    className="text-muted mb-2"
                    style={{
                      color: "#424A53",
                      fontWeight: "500",
                      fontSize: "14px",
                      paddingBottom: "5px",
                    }}
                  >
                    No activity yet
                  </h6>
                  <p
                    className="text-muted small mb-0"
                    style={{
                      color: "#6E7781",
                      fontWeight: "400",
                      fontSize: "10px",
                    }}
                  >
                    Activity and transaction history will appear here once you
                    start working with this Supplier.
                  </p>
                </div>
              </div>
              {/* <img src='/image.png' /> */}
            </div>
          </div>

          {/* Right Column - Contact Information and Supplier Details */}
          <div className="col-lg-4">
            {/* Contact Information Card */}
            <div
              className="mb-4"
              style={{
                border: "1px solid #DCE3EB",
                borderRadius: "14px",
                background: "#fff",
                padding: "16px",
              }}
            >
              <div className="card-header bg-white border-0 pb-0">
                <h5
                  className="mb-0"
                  style={{
                    color: "#424A53",
                    fontWeight: "500",
                    fontSize: "18px",
                    paddingBottom: "16px",
                  }}
                >
                  Contact Information
                </h5>
              </div>
              <div className="card-body">
                {/* Contact Information Section */}
                <div className="mb-4">
                  <div className="mb-5">
                    <div className="d-flex flex-row">
                      <div
                        className="d-flex align-items-center justify-content-center me-3"
                        style={{
                          width: "32px",
                          height: "32px",
                          backgroundColor: "#E3F2FD",
                          borderRadius: "8px",
                        }}
                      >
                        <Mail size={16} style={{ color: "#1976D2" }} />
                      </div>

                      <div className="d-flex flex-column">
                        <span
                          style={{
                            color: "#424A53",
                            fontWeight: "500",
                            fontSize: "14px",
                          }}
                        >
                          Email Address
                        </span>
                        <span
                          style={{
                            color: "#424A53",
                            fontWeight: "400",
                            fontSize: "12px",
                          }}
                        >
                          {vendor.contact_email || vendor.email || "No email provided"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="mb-5">
                    <div className="d-flex flex-row">
                      <div
                        className="d-flex align-items-center justify-content-center me-3"
                        style={{
                          width: "32px",
                          height: "32px",
                          backgroundColor: "#E8F5E8",
                          borderRadius: "8px",
                        }}
                      >
                        <Phone size={16} style={{ color: "#4CAF50" }} />
                      </div>

                      <div className="d-flex flex-column">
                        <span
                          style={{
                            color: "#424A53",
                            fontWeight: "500",
                            fontSize: "14px",
                          }}
                        >
                          Phone Number
                        </span>
                        <span
                          style={{
                            color: "#424A53",
                            fontWeight: "400",
                            fontSize: "12px",
                          }}
                        >
                          {vendor.phone_number || vendor.phone || "No phone provided"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Address Section */}
                <div>
                  <h6
                    className="mb-3"
                    style={{
                      color: "#424A53",
                      fontWeight: "600",
                      fontSize: "16px",
                    }}
                  >
                    Address
                  </h6>

                  <div>
                    <div className="d-flex flex-row">
                      <div
                        className="d-flex align-items-center justify-content-center me-3"
                        style={{
                          width: "32px",
                          height: "32px",
                          backgroundColor: "#FFF3E0",
                          borderRadius: "8px",
                        }}
                      >
                        <MapPin size={16} style={{ color: "#FF9800" }} />
                      </div>

                      <div className="d-flex flex-column">
                        <span
                          style={{
                            color: "#424A53",
                            fontWeight: "500",
                            fontSize: "14px",
                          }}
                        >
                          Business Address
                        </span>
                        <span
                          style={{
                            color: "#424A53",
                            fontWeight: "400",
                            fontSize: "12px",
                          }}
                        >
                          {vendor.address || "No address provided"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description Section */}
                {vendor.description && (
                  <div className="mt-4">
                    <h6
                      className="mb-3"
                      style={{
                        color: "#424A53",
                        fontWeight: "600",
                        fontSize: "16px",
                      }}
                    >
                      Description
                    </h6>
                    <p
                      style={{
                        color: "#424A53",
                        fontWeight: "400",
                        fontSize: "12px",
                        lineHeight: "1.5",
                      }}
                    >
                      {vendor.description}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Supplier Details Card */}
            <div
              className=""
              style={{
                border: "1px solid #DCE3EB",
                borderRadius: "14px",
                background: "#fff",
                padding: "16px",
              }}
            >
              <div className="card-header bg-white border-0 pb-0">
                <h5
                  className="mb-3"
                  style={{
                    color: "#424A53",
                    fontWeight: "500",
                    fontSize: "18px",
                  }}
                >
                  Supplier Details
                </h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <div
                    className="d-flex align-items-center mb-2"
                    style={{
                      color: "#424A53",
                      fontWeight: "500",
                      fontSize: "14px",
                    }}
                  >
                    <Calendar size={16} className="me-2 text-muted" />
                    <span className="text-muted small">Created</span>
                  </div>
                  <p
                    className="mb-0 text-muted"
                    style={{
                      color: "#6E7781",
                      fontWeight: "500",
                      fontSize: "12px",
                    }}
                  >
                    {formatDate(vendor.created_at || vendor.createdAt)}
                  </p>
                </div>

                <div>
                  <div
                    className="d-flex align-items-center mb-2"
                    style={{
                      color: "#424A53",
                      fontWeight: "500",
                      fontSize: "14px",
                    }}
                  >
                    <Calendar size={16} className="me-2 text-muted" />
                    <span className="text-muted small">Last Updated</span>
                  </div>
                  <p
                    className="mb-0 text-muted"
                    style={{
                      color: "#6E7781",
                      fontWeight: "500",
                      fontSize: "12px",
                    }}
                  >
                    {formatDate(vendor.updated_at || vendor.updatedAt)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Supplier Edit Form Modal */}
      {showEditForm && (
        <VendorForm
          vendor={vendor}
          onSave={handleEditVendorSave}
          onCancel={() => setShowEditForm(false)}
        />
      )}
    </>
  );
};

export default VendorDetails;
